import React from 'react';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';

import TimerOutlined from '@mui/icons-material/TimerOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';

import { LandingCard, LandingReveal, LandingRow, landingCaptionSx } from './LandingMotion';

const formats = [
  {
    title: 'Decision Moments',
    tone: 'ready',
    mark: TimerOutlined,
    body:
      'One dilemma in a few minutes. Choose, see the trade-offs, and reflect. Built for brief practice between meetings or workshops.',
  },
  {
    title: 'Decision Games',
    tone: 'ready',
    mark: AccountTreeOutlined,
    body:
      'Richer scenarios with choices, ranking, feedback, and discussion. Individual practice, facilitated sessions, and assessment — powered by the Decision Practice Engine.',
  },
];

const steps = [
  {
    title: 'Bring context and knowledge',
    tone: 'ready',
    mark: MenuBookOutlined,
    body:
      'Your situation, your audience, and the expertise the decision should draw on.',
  },
  {
    title: 'Shape the experience',
    tone: 'ready',
    mark: TuneOutlined,
    body:
      'Itclarifies builds scenarios, decisions, and feedback from your context. You review and publish.',
  },
  {
    title: 'Decide and reflect',
    tone: 'done',
    mark: ForumOutlined,
    body:
      'People choose or rank, explain their reasoning, and learn from feedback — in teams or individually, online or in person.',
  },
];

const DecisionWalkthrough = () => (
  <Box
    id="how-it-works"
    component="section"
    sx={{
      bgcolor: 'background.paper',
      py: { xs: 8, md: 12 },
      borderTop: '1px solid',
      borderColor: 'divider',
      scrollMarginTop: 88,
    }}
  >
    <Container maxWidth="lg">
      <LandingReveal>
        <Typography variant="caption" sx={landingCaptionSx}>
          How it works
        </Typography>
        <Typography
          component="h2"
          sx={{
            mt: 1,
            mb: 1.5,
            fontWeight: 600,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            letterSpacing: '-0.02em',
            maxWidth: 560,
          }}
        >
          Two ways to practise decisions
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 560, fontSize: { sm: '1.05rem' } }}
        >
          Short Decision Moments and deeper Decision Games share the same idea:
          put people in a situation where judgment matters.
        </Typography>
      </LandingReveal>

      <Grid container spacing={2.5} sx={{ mb: 8 }}>
        {formats.map((format, index) => (
          <Grid item xs={12} sm={6} key={format.title} sx={{ display: 'flex' }}>
            <LandingReveal index={index} sx={{ width: '100%', height: '100%' }}>
              <LandingCard
                kind="choice"
                mark={format.mark}
                tone={format.tone}
                title={format.title}
                body={format.body}
                titleComponent="h3"
              />
            </LandingReveal>
          </Grid>
        ))}
      </Grid>

      <LandingReveal>
        <Typography
          component="h3"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.2rem', md: '1.35rem' },
            mb: 2,
          }}
        >
          From knowledge to practice
        </Typography>
      </LandingReveal>
      <Stack spacing={4}>
        {steps.map((step, index) => (
          <LandingReveal key={step.title} index={index}>
            <LandingRow
              mark={step.mark}
              tone={step.tone}
              title={step.title}
              body={step.body}
              titleComponent="h3"
            />
          </LandingReveal>
        ))}
      </Stack>
    </Container>
  </Box>
);

export default DecisionWalkthrough;
